import { Box, FormField, Input, ImageViewer, Divider } from '@wix/design-system';
import React, { useContext, useState } from 'react';
import { RicosContext, ModalContext, PluginsEventsContext, EditorContext, ZIndexContext } from 'ricos-context';
import { getImageSrc } from 'wix-rich-content-common/libs/imageUtils';
import { SidePanelLayout, ToggleButton, FooterActions } from 'wix-rich-content-ui-components';
import { s as styleInject } from '../style-inject.es-e7b07dd7.js';
import { AUDIO_ACTION_NAMES, AUDIO_BI_VALUES, AUDIO_TYPES, audioModals } from '../consts.js';
import { AUDIO_SETTINGS_MODAL, AUDIO_SETTINGS_AUDIO_NAME_INPUT, AUDIO_SETTINGS_AUTHOR_NAME_INPUT, AUDIO_COVER_IMAGE, AUDIO_DOWNLOAD_TOGGLE } from '../dataHooks.js';
import { AUDIO_TYPE } from 'ricos-content';
import 'ricos-types';

var css_248z = ".ov2Fr{--wsr-color-D10-66:rgba(0,0,0,.5)}[dir] .ov2Fr{background-color:unset}";
var classes = {"imageViewer":"ov2Fr"};
styleInject(css_248z);

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const AudioSettingsModal = ({ nodeId, onFileSelect }) => {
  var _a, _b;
  const { t, environment } = useContext(RicosContext);
  const modalService = useContext(ModalContext) || {};
  const pluginsEvents = useContext(PluginsEventsContext);
  const { getEditorCommands } = useContext(EditorContext);
  const zIndexService = useContext(ZIndexContext);
  const [initialData] = useState(
    () => getEditorCommands().getBlockComponentData(nodeId)
  );
  const componentData = getEditorCommands().getBlockComponentData(nodeId);
  const updateData = (data) => {
    var _a2, _b2, _c, _d, _e, _f;
    (_f = pluginsEvents.publishMediaPluginsDetails) == null ? void 0 : _f.call(pluginsEvents, {
      pluginId: AUDIO_TYPE,
      nodeId,
      creator: data.authorName,
      title: data.name,
      track_duration: (_b2 = (_a2 = data.audio) == null ? void 0 : _a2.duration) == null ? void 0 : _b2.toString(),
      type: AUDIO_TYPES.custom,
      url: (_e = (_d = (_c = data.audio) == null ? void 0 : _c.src) == null ? void 0 : _d.id) != null ? _e : ""
    });
    queueMicrotask(() => {
      getEditorCommands().setBlock(nodeId, AUDIO_TYPE, __spreadProps(__spreadValues(__spreadValues({}, componentData), data), { id: nodeId }));
    });
  };
  const closeModal = () => {
    modalService.closeModal(audioModals.settings);
  };
  const handleOnClose = () => {
    updateData(initialData);
    closeModal();
  };
  const imgSrc = componentData.coverImage && getImageSrc(
    { file_name: ((_a = componentData.coverImage.src) == null ? void 0 : _a.id) || ((_b = componentData.coverImage) == null ? void 0 : _b.file_name) },
    void 0,
    {
      requiredWidth: 142,
      requiredHeight: 133,
      requiredQuality: 70
    }
  );
  const handleCoverImageDelete = () => {
    var _a2;
    updateData(__spreadProps(__spreadValues({}, componentData), { coverImage: null }));
    (_a2 = pluginsEvents.publishPluginChangeSettings) == null ? void 0 : _a2.call(pluginsEvents, {
      nodeId,
      pluginId: AUDIO_TYPE,
      actionName: AUDIO_ACTION_NAMES.changeCover,
      value: AUDIO_BI_VALUES.NO
    });
  };
  const onInputBlur = (actionName, value) => {
    var _a2;
    (_a2 = pluginsEvents.publishPluginChangeSettings) == null ? void 0 : _a2.call(pluginsEvents, {
      nodeId,
      pluginId: AUDIO_TYPE,
      actionName,
      value
    });
  };
  return /* @__PURE__ */ React.createElement(
    SidePanelLayout,
    {
      onCloseButtonClick: handleOnClose,
      dataHook: AUDIO_SETTINGS_MODAL,
      header: { title: t("AudioPlugin_Settings_Header") },
      content: {
        children: /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP4" }, /* @__PURE__ */ React.createElement(FormField, { label: t("AudioPlugin_Settings_AudioName_Label") }, /* @__PURE__ */ React.createElement(
          Input,
          {
            placeholder: t("AudioPlugin_Settings_AudioName_Label_Placeholder"),
            value: componentData.name,
            onBlur: (e) => {
              onInputBlur(AUDIO_ACTION_NAMES.changeTitle, e.target.value);
            },
            onChange: (e) => {
              updateData(__spreadProps(__spreadValues({}, componentData), {
                name: e.target.value
              }));
            },
            dataHook: AUDIO_SETTINGS_AUDIO_NAME_INPUT,
            size: "small"
          }
        )), /* @__PURE__ */ React.createElement(FormField, { label: t("AudioPlugin_Settings_AuthorName_Label") }, /* @__PURE__ */ React.createElement(
          Input,
          {
            placeholder: t("AudioPlugin_Settings_AuthorName_Label_Placeholder"),
            value: componentData.authorName,
            onBlur: (e) => {
              onInputBlur(AUDIO_ACTION_NAMES.changeCreator, e.target.value);
            },
            onChange: (e) => {
              updateData(__spreadProps(__spreadValues({}, componentData), {
                authorName: e.target.value
              }));
            },
            dataHook: AUDIO_SETTINGS_AUTHOR_NAME_INPUT,
            size: "small"
          }
        )), /* @__PURE__ */ React.createElement(FormField, { label: t("AudioPlugin_Settings_CoverImage_Label") }, /* @__PURE__ */ React.createElement(
          ImageViewer,
          {
            className: environment === "liveSite" ? classes.imageViewer : void 0,
            dataHook: AUDIO_COVER_IMAGE,
            imageUrl: imgSrc,
            width: 142,
            height: 133,
            removeRoundedBorders: environment === "liveSite",
            onAddImage: () => {
              var _a2;
              (_a2 = pluginsEvents.publishPluginChangeSettings) == null ? void 0 : _a2.call(pluginsEvents, {
                pluginId: AUDIO_TYPE,
                nodeId,
                actionName: AUDIO_ACTION_NAMES.changeCover,
                value: AUDIO_BI_VALUES.YES
              });
              onFileSelect();
            },
            onUpdateImage: () => {
              var _a2;
              (_a2 = pluginsEvents.publishPluginChangeSettings) == null ? void 0 : _a2.call(pluginsEvents, {
                pluginId: AUDIO_TYPE,
                nodeId,
                actionName: AUDIO_ACTION_NAMES.changeCover,
                value: AUDIO_BI_VALUES.YES
              });
              onFileSelect(0);
            },
            onRemoveImage: handleCoverImageDelete,
            tooltipProps: {
              appendTo: (el) => {
                var _a2;
                return Boolean((_a2 = el.getAttribute("data-hook")) == null ? void 0 : _a2.includes("ricos-portal"));
              },
              zIndex: zIndexService.getZIndex("TOOLTIP")
            }
          }
        )), /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(
          ToggleButton,
          {
            checked: !componentData.disableDownload,
            title: t("AudioPlugin_Settings_AudioCanBeDownloaded_Label"),
            dataHook: AUDIO_DOWNLOAD_TOGGLE,
            onChange: () => {
              var _a2;
              const disableDownload = !componentData.disableDownload;
              (_a2 = pluginsEvents.publishPluginChangeSettings) == null ? void 0 : _a2.call(pluginsEvents, {
                pluginId: AUDIO_TYPE,
                nodeId,
                actionName: AUDIO_ACTION_NAMES.downloadAudio,
                value: disableDownload ? AUDIO_BI_VALUES.YES : AUDIO_BI_VALUES.NO
              });
              updateData(__spreadProps(__spreadValues({}, componentData), { disableDownload }));
            },
            infoContent: t("AudioPlugin_Settings_AudioCanBeDownloaded_Tooltip")
          }
        ))
      },
      footer: {
        children: /* @__PURE__ */ React.createElement(FooterActions, { onCancel: handleOnClose, onSave: closeModal })
      }
    }
  );
};

export { AudioSettingsModal };
//# sourceMappingURL=SettingsModal.js.map
